import { Box, Typography, Colors } from "@analyticsMD/quil";
import ErrorOutlineIcon from "@mui/icons-material/Error";
import { css } from "@emotion/css";
import CollapsibleSection from "../shared/collapsible-section";

import type { FC } from "react";
import type {
  MalnutritionCriterion,
  MalnutritionCriterionMetric,
  MalnutritionCriterionQuote,
  MalnutritionStatus,
} from "../../types/Malnutrition";

const FLAGGED_RED = "#C62B2B";
const PROTOCOL_BLUE = "#2D4FBE";

type Variant = "flagged" | "protocol";

const variantFromStatus = (status: MalnutritionStatus): Variant =>
  status === "protocol" ? "protocol" : "flagged";

interface VariantTheme {
  accent: string;
  headerLabel: string;
  summaryLabel: (n: number) => string;
}

const VARIANTS: Record<Variant, VariantTheme> = {
  flagged: {
    accent: FLAGGED_RED,
    headerLabel: "WHY FLAGGED",
    summaryLabel: (n) => `· ${n} criteria met`,
  },
  protocol: {
    accent: PROTOCOL_BLUE,
    headerLabel: "PROTOCOL TRIGGER",
    summaryLabel: (n) => `· ${n} rules met`,
  },
};

const styles = {
  card: (accent: string) => css`
    background: ${Colors.white};
    border-left: 3px solid ${accent};
    border-radius: 0.375rem;
    padding: 0.75rem 0.875rem;
    margin-bottom: 0.625rem;
  `,
  quoteBlock: css`
    background: #f4eef9;
    border-left: 3px solid #a585c8;
    padding: 0.625rem 0.75rem;
    margin: 0.5rem 0 0.625rem 0;
    font-style: italic;
    color: ${Colors.grey_80};
    border-radius: 0.125rem;
  `,
  source: css`
    color: ${Colors.grey_40};
    font-size: 0.75rem;
  `,
  metricRow: css`
    display: flex;
    gap: 0.375rem;
    font-size: 0.85rem;
    line-height: 1.45;
  `,
};

const cardSxStyles = {
  title: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: Colors.grey_80,
    marginBottom: "0.375rem",
  },
};

const metricSx = (accent: string) => ({
  label: {
    fontSize: "0.85rem",
    color: accent,
    fontWeight: 600,
  },
  value: {
    fontSize: "0.85rem",
    color: accent,
  },
});

const headingSx = (accent: string) => ({
  fontSize: "0.8125rem",
  fontWeight: 700,
  color: accent,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  display: "flex",
  alignItems: "center",
});

const iconSx = (accent: string) => ({
  backgroundColor: accent,
  color: Colors.white,
  borderRadius: "0.25rem",
  padding: "0.125rem",
  fontSize: "1rem",
  marginRight: "0.5rem",
});

const MetricCriterionCard: FC<{
  criterion: MalnutritionCriterionMetric;
  accent: string;
}> = ({ criterion, accent }) => {
  const sx = metricSx(accent);
  return (
    <Box className={styles.card(accent)}>
      <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
      {criterion.rows.map((row, idx) => (
        <Box key={idx} className={styles.metricRow}>
          <Box component="span" sx={sx.label}>
            {row.label}:
          </Box>
          <Box component="span" sx={sx.value}>
            {row.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const QuoteCriterionCard: FC<{
  criterion: MalnutritionCriterionQuote;
  accent: string;
}> = ({ criterion, accent }) => (
  <Box className={styles.card(accent)}>
    <Typography sx={cardSxStyles.title}>{criterion.title}</Typography>
    <Box className={styles.quoteBlock}>&ldquo;{criterion.quote}&rdquo;</Box>
    <Box className={styles.source}>
      {criterion.source_kind} · {criterion.source_author} ·{" "}
      {criterion.source_timestamp}
    </Box>
  </Box>
);

export const CriterionCard: FC<{
  criterion: MalnutritionCriterion;
  variant?: Variant;
}> = ({ criterion, variant = "flagged" }) => {
  const accent = VARIANTS[variant].accent;
  if (criterion.kind === "metric") {
    return <MetricCriterionCard criterion={criterion} accent={accent} />;
  }
  return <QuoteCriterionCard criterion={criterion} accent={accent} />;
};

interface WhyFlaggedSectionProps {
  criteria: MalnutritionCriterion[];
  status: MalnutritionStatus;
}

const WhyFlaggedSection: FC<WhyFlaggedSectionProps> = ({ criteria, status }) => {
  const variant = variantFromStatus(status);
  const theme = VARIANTS[variant];
  const count = criteria.length;
  return (
    <CollapsibleSection
      defaultOpen
      title={theme.headerLabel}
      icon={<ErrorOutlineIcon sx={iconSx(theme.accent)} />}
      summary={<>{theme.summaryLabel(count)}</>}
      headingTextSx={headingSx(theme.accent)}
      outerClassName={css`background: #f4f1f6; border-radius: 0.5rem; margin: 0 0.75rem 0.75rem;`}
      dataTestId={
        variant === "protocol" ? "protocol-trigger-section" : "why-flagged-section"
      }
    >
      {criteria.map((criterion, idx) => (
        <CriterionCard key={idx} criterion={criterion} variant={variant} />
      ))}
    </CollapsibleSection>
  );
};

export default WhyFlaggedSection;
